import {useEffect, useState} from "react";
import {Plus, MapPin, Users as UsersIcon, Clock, Lock, Globe, CalendarDays} from "lucide-react";
import {apiGet, apiPost} from "@/lib/api";
import {FormModal} from "@/components/UI";
import {useSite, Rich} from "@/lib/site";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CATEGORIES = ["Garba / Navratri", "Mela / Festival", "Satsang / Seva", "Business meetup", "Food & Social", "Other"];
const VIS_LABEL = {invite_only: [Lock, "Invite-only"], public_in_app: [Globe, "App-public"], public_listing: [Globe, "Public listing"]};

function EventCard({ev, onRsvp, showStatus}) {
  const dt = new Date(ev.when);
  const [VisIcon, visText] = VIS_LABEL[ev.visibility] || VIS_LABEL.invite_only;
  return (
    <article className="event-card" data-testid={`event-${ev.event_id}`}>
      <div className="event-date-chip">
        <b>{dt.getDate().toString().padStart(2,"0")}</b>
        <small>{MONTHS[dt.getMonth()]}</small>
      </div>
      <div className="event-body">
        <div className="row-meta">
          {ev.category && <span className="pill ghost">{ev.category}</span>}
          {ev.city && <span className="pill" data-testid={`event-city-${ev.event_id}`}>{ev.city}</span>}
          {ev.circle_name && <span className="pill ghost" data-testid={`event-circle-${ev.event_id}`}>{ev.circle_name}</span>}
          {showStatus && ev.listing_status && ev.listing_status !== "approved" && <span className="pill ghost" data-testid={`event-status-${ev.event_id}`}>{ev.listing_status === "pending" ? "Awaiting approval" : "Not approved"}</span>}
        </div>
        <h3>{ev.title}</h3>
        <p>{ev.description || "A gathering."}</p>
        <div className="event-meta">
          <span><Clock size={12}/> {dt.toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})} · {dt.toDateString()}</span>
          {ev.location && <span><MapPin size={12}/> {ev.location}</span>}
          <span><VisIcon size={12}/> {visText}</span>
          <span><UsersIcon size={12}/> Host: {ev.host_name}{ev.going ? ` · ${ev.going} going` : ""}</span>
        </div>
      </div>
      <div className="event-rsvp">
        {["going","maybe","declined"].map(s => (
          <button key={s} data-testid={`rsvp-${ev.event_id}-${s}`} className={ev.my_rsvp === s ? "rsvp-btn active" : "rsvp-btn"} onClick={() => onRsvp(ev, s)}>
            {s === "going" ? "Going" : s === "maybe" ? "Maybe" : "Can't"}
          </button>
        ))}
      </div>
    </article>
  );
}

function NearMe({user, rsvp, setRsvpTarget}) {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState(() => localStorage.getItem("io-event-city") || "");
  const [events, setEvents] = useState(null);

  useEffect(() => { apiGet("/public-events/cities").then(setCities).catch(() => {}); }, []);
  useEffect(() => {
    localStorage.setItem("io-event-city", city);
    apiGet("/public-events", {city}).then(setEvents).catch(() => setEvents([]));
  }, [city, user]);
  useEffect(() => { setRsvpTarget(() => setEvents); }, [setRsvpTarget]);

  return (
    <>
      <div className="city-filter" data-testid="events-city-filter">
        <MapPin size={15}/>
        <select data-testid="events-city-select" value={city} onChange={e => setCity(e.target.value)}>
          <option value="">All cities</option>
          {cities.map(c => <option key={c}>{c}</option>)}
        </select>
        <div className="city-chips">
          {cities.slice(0, 6).map(c => (
            <button key={c} data-testid={`events-city-${c.toLowerCase()}`} className={city === c ? "tag-chip active" : "tag-chip"} onClick={() => setCity(city === c ? "" : c)}>{c}</button>
          ))}
        </div>
      </div>
      {events && events.length === 0 ? (
        <div className="empty-pane" data-testid="public-events-empty"><CalendarDays size={26}/><h3>No public events {city ? `in ${city}` : ""} yet</h3><p>Hosting a garba night or mela? List it for everyone — admin reviews it first.</p></div>
      ) : (
        <div className="events-grid" data-testid="public-events-list">
          {(events || []).map(ev => <EventCard key={ev.event_id} ev={ev} onRsvp={rsvp}/>)}
        </div>
      )}
    </>
  );
}

export default function Events({notify, user, requireAuth, setModal, communities}) {
  const t = useSite();
  const [tab, setTab] = useState("near");
  const [events, setEvents] = useState([]);
  const [composer, setComposer] = useState(false);
  const [publicSetter, setPublicSetter] = useState(null);

  useEffect(() => { if (user) apiGet("/events").then(setEvents).catch(() => setEvents([])); else setEvents([]); }, [user]);
  const mine = events.filter(e => e.visibility !== "public_listing" || e.host_id === user?.user_id || e.my_rsvp);

  const rsvp = (ev, status) => requireAuth(async () => {
    try {
      await apiPost(`/events/${ev.event_id}/rsvp`, {status});
      const upd = list => list.map(e => e.event_id === ev.event_id ? {...e, my_rsvp: status} : e);
      setEvents(upd);
      if (publicSetter) publicSetter(upd);
      notify(status === "going" ? "Chalo! See you there" : `RSVP: ${status}`);
    } catch { notify("Please try again"); }
  });

  const created = (e) => {
    setEvents(prev => [e, ...prev]); setComposer(false); setTab("mine");
    notify(e.visibility === "public_listing" ? "Sent for approval — it goes live once admin approves" : "Event created — invitees can RSVP");
  };

  return (
    <div data-testid="events-page">
      <div className="page-title">
        <div>
          <div className="eyebrow">EVENTS AROUND · CHALO, MALIYE</div>
          <h1><Rich text={t("events_title")}/></h1>
          <p>{t("events_sub")}</p>
        </div>
        <button data-testid="events-create" className="coral-button" onClick={() => requireAuth(() => setComposer(true))}>
          <Plus size={16}/> Host an event
        </button>
      </div>

      <div className="activity-tabs" data-testid="events-tabs">
        <button data-testid="events-tab-near" className={tab === "near" ? "activity-tab active" : "activity-tab"} onClick={() => setTab("near")}>Near me</button>
        <button data-testid="events-tab-mine" className={tab === "mine" ? "activity-tab active" : "activity-tab"} onClick={() => setTab("mine")}>My events</button>
      </div>

      {tab === "near" ? <NearMe user={user} rsvp={rsvp} setRsvpTarget={setPublicSetter}/> : !user ? (
        <div className="empty-pane"><h3>Sign in to see your events</h3><p>Your invitations and hosted events show up here.</p><button data-testid="events-signin" className="coral-button" onClick={() => setModal("auth")}>Sign in</button></div>
      ) : mine.length === 0 ? (
        <div className="empty-pane"><h3>No events yet</h3><p>Host your first event — the people you invite will see it here.</p><button data-testid="events-empty-create" className="coral-button" onClick={() => setComposer(true)}><Plus size={15}/> Host an event</button></div>
      ) : (
        <div className="events-grid">
          {mine.map(ev => <EventCard key={ev.event_id} ev={ev} onRsvp={rsvp} showStatus/>)}
        </div>
      )}

      {composer && <ComposeEvent close={() => setComposer(false)} onCreated={created} notify={notify} circles={(communities || []).filter(c => c.my_status === "member")}/>}
    </div>
  );
}

function ComposeEvent({close, onCreated, notify, circles}) {
  const [f, setF] = useState({title:"", description:"", when:"", location:"", city:"", category: CATEGORIES[0], visibility:"invite_only", invitees:"", circle_id: ""});
  const ch = e => setF({...f, [e.target.name]: e.target.value});
  const isPublic = f.visibility === "public_listing";
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {...f, invitees: f.invitees.split(/[\s,]+/).filter(Boolean), when: new Date(f.when).toISOString()};
      onCreated(await apiPost("/events", payload));
    } catch { notify("Please check the details and try again"); }
  };
  return (
    <FormModal title="Host an event" eyebrow="NEW EVENT" close={close} testId="event-compose">
      <p className="modal-sub">{isPublic ? "Public listings show to everyone in that city after admin approval." : "Only invitees see the details. We'll remind guests before the event."}</p>
      <form onSubmit={submit}>
        <label>Who can see it?
          <select data-testid="event-visibility" name="visibility" value={f.visibility} onChange={ch}>
            <option value="invite_only">Invite-only</option>
            <option value="public_in_app">App-public (any member)</option>
            <option value="public_listing">Public listing — everyone near the city (admin approves)</option>
          </select>
        </label>
        <label>Title<input data-testid="event-title" required name="title" value={f.title} onChange={ch} placeholder="Navratri garba night, Diwali mela..."/></label>
        <div className="form-row">
          <label>When<input data-testid="event-when" required type="datetime-local" name="when" value={f.when} onChange={ch}/></label>
          <label>Type
            <select data-testid="event-category" name="category" value={f.category} onChange={ch}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
          </label>
        </div>
        <div className="form-row">
          <label>City{isPublic ? " (required)" : ""}<input data-testid="event-city" required={isPublic} name="city" value={f.city} onChange={ch} placeholder="Edison, Leicester..."/></label>
          <label>Venue<input data-testid="event-location" name="location" value={f.location} onChange={ch} placeholder="Hall or address"/></label>
        </div>
        {!isPublic && (
          <label>Invitee emails (comma or newline separated)
            <textarea data-testid="event-invitees" name="invitees" value={f.invitees} onChange={ch} placeholder="friend@example.com, sister@example.com"/>
          </label>
        )}
        <label>Tag a samaj (all its members get an invite)
          <select data-testid="event-circle" name="circle_id" value={f.circle_id} onChange={ch}>
            <option value="">No samaj</option>
            {circles.map(c => <option key={c.community_id} value={c.community_id}>{c.name}</option>)}
          </select>
        </label>
        <label>Description<textarea data-testid="event-description" name="description" value={f.description} onChange={ch} placeholder="Live dhol? Dress code? Bring something?"/></label>
        <button data-testid="event-submit" className="coral-button full" type="submit">{isPublic ? "Send for approval" : "Create & send invitations"}</button>
      </form>
    </FormModal>
  );
}
